import 'reflect-metadata';

import {
  IMicrositeHomePageConfig,
  IMicrositePageConfiguration,
  IMicrositeTopicPageConfig,
  IPageDynamicBreadcrumbsConfiguration,
} from 'src/interfaces/page/page.interface';

import { IMicrositeConfiguration } from 'src/interfaces/microsite';
import { injectable } from 'inversify';
import ApiClient from './api-client';
import { IMicrositeService } from './interfaces';

@injectable()
class MicrositeService implements IMicrositeService {
  private apiClient: ApiClient;

  public constructor() {
    this.apiClient = new ApiClient(process.env.MICROSITE_API_BASE_URL ?? '');
  }

  public async getPageDynamicBreadcrumbs(
    tenantId: string,
    packageId: string,
    pageId: string
  ): Promise<IPageDynamicBreadcrumbsConfiguration[] | undefined> {
    const response =
      await this.apiClient.get<IPageDynamicBreadcrumbsConfigurationDataWrapper>(
        `tenants/${tenantId}/packages/${packageId}/products/microsite/page/${pageId}/breadcrumbs`
      );

    const breadcrumbs = response?.data?.breadcrumbs;

    return breadcrumbs;
  }

  public async getTopicPage(
    tenantId: string,
    packageId: string,
    urlSlug: string,
    language?: string
  ): Promise<IMicrositeTopicPageConfig | undefined> {
    const response = await this.apiClient.get<IMicrositePageDataWrapper>(
      `tenants/${tenantId}/packages/${packageId}/products/microsite/page?urlSlug=${urlSlug}&type=Topic${
        language ? `&language=${language.toLocaleLowerCase()}` : ''
      }`
    );

    const topicPage = response?.data?.pages?.find(
      (page) => !page.deleted
    ) as IMicrositeTopicPageConfig;

    return topicPage;
  }

  public async getPageBySlug(
    tenantId: string,
    packageId: string,
    urlSlug: string[],
    language?: string
  ): Promise<IMicrositePageConfiguration | undefined> {
    const urlSlugQueryParam = urlSlug.join('/');
    const response = await this.apiClient.get<IMicrositePageDataWrapper>(
      `tenants/${tenantId}/packages/${packageId}/products/microsite/page?urlSlug=${encodeURIComponent(
        urlSlugQueryParam
      )}${language ? `&language=${language.toLocaleLowerCase()}` : ''}`
    );

    const page = response?.data?.pages?.find((p) => !p.deleted);

    return page;
  }

  public async getMicrosite(
    tenantId: string,
    packageId: string,
    language?: string
  ): Promise<IMicrositeConfiguration | undefined> {
    const response =
      await this.apiClient.get<IMicrositeConfigurationDataWrapper>(
        `tenants/${tenantId}/packages/${packageId}/products/microsite${
          language ? `?language=${language.toLocaleLowerCase()}` : ''
        }`
      );

    return response?.data?.microsite;
  }

  public async getHomePage(
    tenantId: string,
    packageId: string,
    language?: string
  ): Promise<IMicrositeHomePageConfig | undefined> {
    const response = await this.apiClient.get<IMicrositePageDataWrapper>(
      `tenants/${tenantId}/packages/${packageId}/products/microsite/page?type=Home${
        language ? `&language=${language.toLocaleLowerCase()}` : ''
      }`
    );

    const homePage = response?.data?.pages?.find(
      (page) => !page.deleted
    ) as IMicrositeHomePageConfig;

    return homePage;
  }
}

interface IMicrositeConfigurationDataWrapper {
  data?: { microsite: IMicrositeConfiguration };
  status: string;
}

interface IMicrositePageDataWrapper {
  data?: { pages: IMicrositePageConfiguration[] };
  status: string;
}

interface IPageDynamicBreadcrumbsConfigurationDataWrapper {
  data?: { breadcrumbs: IPageDynamicBreadcrumbsConfiguration[] };
  status: string;
}

export default MicrositeService;
